import React, { useContext } from "react";
import parse from "html-react-parser";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import Collapse, { Panel } from "rc-collapse";
import SimpleBar from "simplebar-react";
import "./TeacherInfoPanelContent.scss";
import { configStore } from "../../../../../../contexts/ConfigContext";
import {
    getItemDescription,
    getResourceIndexFromPool,
    getResourceIndex,
    initialTestFilter,
} from "../../../../../../utils/dataRetrieval";
import { commonMessages } from "../../../../../../utils/messages";
import { useResourceSeriesMOA } from "../../../../../../components/Player/ResourceSeries/utils/moaResourceSeriesUtils";

import arrow from "./arrow.svg";
import "rc-drawer/assets/index.css";
import "rc-collapse/assets/index.css";
import { Activity, Module, Objective } from "@evidenceb/gameplay-interfaces";
import { AIConfig } from "@evidenceb/ai-handler";
import { Data } from "../../../../../../interfaces/Data";
import { useData } from "../../../../../../hooks/useData";

interface InfoPanelContentProps {
    className?: string;
}

const TeacherInfoPanelContent = ({ className }: InfoPanelContentProps) => {
    const {
        config: { ai },
    } = useContext(configStore);
    const intl = useIntl();
    const data = useData();
    const { module, objective, activity } = useResourceSeriesMOA();
    if (!objective || !activity) return null;

    const objectiveDescription = getItemDescription(objective!, "teacher");
    const activityDescription = getItemDescription(activity!, "teacher");

    if (
        !objectiveDescription &&
        !objective!.targetedDifficulties?.$html &&
        !activityDescription
    )
        return null;

    return (
        <SimpleBar className={`teacher-info__drawer__content ${className}`}>
            <Collapse
                expandIcon={CollapseExpandIcon}
                accordion={false}
                defaultActiveKey={["objective", "activity"]}
            >
                {(objectiveDescription !== "" ||
                    objective!.targetedDifficulties?.$html) && (
                    <Panel
                        header={generateObjectiveTitle(
                            module,
                            objective!,
                            activity,
                            data,
                            ai,
                            intl
                        )}
                        className="title"
                        key={"objective"}
                    >
                        <div className="panel-section">
                            <p className="description">
                                {parse(objectiveDescription)}
                            </p>
                        </div>
                        {objective!.targetedDifficulties?.$html && (
                            <div className="panel-section">
                                <p className="sub-title">
                                    <FormattedMessage
                                        id="drawer-targetedDifficulties"
                                        defaultMessage="Targeted difficulties"
                                    />
                                </p>
                                <p className="description">
                                    {parse(
                                        objective!.targetedDifficulties?.$html
                                    )}
                                </p>
                            </div>
                        )}
                    </Panel>
                )}
                {activityDescription !== "" && (
                    <Panel
                        header={generateActivityTitle(
                            objective,
                            activity,
                            intl
                        )}
                        className="title"
                        key={"activity"}
                    >
                        <div className="panel-section">
                            <p className="description">
                                {parse(activityDescription)}
                            </p>
                        </div>
                    </Panel>
                )}
            </Collapse>
        </SimpleBar>
    );
};

const CollapseExpandIcon = (props: any) => {
    return (
        <img
            style={{
                verticalAlign: "-.125em",
                transition: "transform .2s",
                transform: `rotate(${props.isActive ? 180 : 0}deg)`,
            }}
            className={"custom-arrow"}
            alt="collapse"
            src={arrow}
        />
    );
};

export default TeacherInfoPanelContent;

const generateObjectiveTitle = (
    module: Module,
    objective: Objective,
    activity: Activity,
    data: Data,
    ai: AIConfig,
    intl: IntlShape
) => {
    let initialTestId = module.objectiveIds.find(
        initialTestFilter(ai.initialTest, "isInitialTest")
    );
    let objectiveIntl = intl.formatMessage({
        id: "hierarchy-objective-full",
        defaultMessage: "objective",
    });
    if (!objective || !activity)
        throw new Error("Shouldn't arrive here without activity of objective");
    if (initialTestId) {
        if (initialTestId === objective.id) return `${objective!.title.long}`;
        return `${objectiveIntl} ${getResourceIndex(
            objective!,
            data,
            "objective"
        )}`;
    }
    return `${objectiveIntl} ${
        getResourceIndexFromPool(objective!.id, module.objectiveIds) + 1
    }`;
};

const generateActivityTitle = (
    objective: Objective,
    activity: Activity,
    intl: IntlShape
) => {
    return `${intl.formatMessage(commonMessages.activity)} ${
        getResourceIndexFromPool(activity!.id, objective!.activityIds) + 1
    }`;
};
